import React, { useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Dialog,
    DialogContent,
    Grid,
    TextField,
    makeStyles,
} from '@material-ui/core';
import { Col, Form } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import ButtonStyles from 'styles/theme/Button';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import Select from 'components/widgets/Select';
import ReportTemplateMutation from 'services/graphQL/mutate/setting/ReportTemplateMutation';

const useStyles = makeStyles((theme) => ({
    content: {
        paddingTop: theme.spacing(1),
    },
    input: {
        fontSize: 13,
    },
    ...ButtonStyles.getStyle(theme),
}));

const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Daily' },
];

const INIT_FORM = {
    name: '',
    query: '',
    content: '',
    frequency: 'daily',
    timeScheduled: '09:00',
};

const ACTION_TYPES = {
    CHANGE_FIELD: 'changeField',
    RESET_FORM: 'resetForm',
};

const reducer = (state, action = {}) => {
    switch (action.type) {
    case ACTION_TYPES.CHANGE_FIELD:
        return {
            ...state,
            [action.value.name]: action.value.value,
        };
    case ACTION_TYPES.RESET_FORM:
        return { ...INIT_FORM };
    default:
        return state;
    }
};

const CreateReportDialog = ({
    open,
    onClose,
    onCreated,
}) => {
    const classes = useStyles();
    const [form, dispatch] = useReducer(reducer, INIT_FORM);

    useEffect(() => {
        if (!open) {
            dispatch({ type: ACTION_TYPES.RESET_FORM });
        }
    }, [open]);

    const [createReport, { loading }] = useMutation(ReportTemplateMutation.REPORT_TEMPLATE_CREATE, {
        onCompleted: (response) => {
            if (response?.reportTemplateCreate?.success) {
                ModalUtils.successMessage(null, 'Report created successfully');
                onCreated();
                onClose();
                return;
            }

            ModalUtils.errorMessage(null, 'Only queries of type SELECT are allowed.');
        },
        onError: (error) => {
            ModalUtils.errorMessage(error?.graphQLErrors, error?.message);
        },
    });

    const isValid = useMemo(() => (
        !StringUtils.isEmpty(form.name?.trim())
        && !StringUtils.isEmpty(form.query?.trim())
        && !StringUtils.isEmpty(form.content?.trim())
        && !StringUtils.isEmpty(form.frequency?.trim())
        && !StringUtils.isEmpty(form.timeScheduled?.trim())
    ), [form]);

    const onChange = (field, value) => {
        dispatch({
            type: ACTION_TYPES.CHANGE_FIELD,
            value: { name: field, value },
        });
    };

    const onSave = () => {
        if (!isValid) return;

        createReport({
            variables: {
                name: form.name.trim(),
                query: form.query.trim(),
                content: form.content.trim(),
                frequency: form.frequency,
                timeScheduled: form.timeScheduled,
            },
        });
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            disableBackdropClick
            disableEscapeKeyDown
        >
            <DialogAppBar
                title="New Report"
                onClose={onClose}
                disabled={loading}
                toolbarSize="md"
            />
            <DialogContent>
                <Box className={classes.content}>
                    <Form.Group as={Col} controlId="create-report-name">
                        <Form.Label>Report Name</Form.Label>
                        <Form.Control
                            className={classes.input}
                            value={form.name}
                            onChange={(e) => onChange('name', e.target.value)}
                            maxLength={160}
                        />
                    </Form.Group>
                    <Form.Group as={Col} controlId="create-report-query">
                        <Form.Label>Query (SELECT only)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            className={classes.input}
                            value={form.query}
                            onChange={(e) => onChange('query', e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group as={Col} controlId="create-report-template">
                        <Form.Label>{`Template (${form.content.length}/255)`}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            className={classes.input}
                            value={form.content}
                            maxLength={255}
                            onChange={(e) => onChange('content', e.target.value)}
                        />
                    </Form.Group>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Form.Group as={Col}>
                                <Form.Label>Frequency</Form.Label>
                                <Select
                                    size="sm"
                                    name="frequency"
                                    className={classes.input}
                                    value={form.frequency}
                                    options={FREQUENCY_OPTIONS}
                                    aria-label="Frequency"
                                    onChange={(name, value) => onChange(name, value)}
                                />
                            </Form.Group>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Form.Group as={Col}>
                                <Form.Label>At</Form.Label>
                                <TextField
                                    type="time"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.timeScheduled}
                                    onChange={(e) => onChange('timeScheduled', e.target.value)}
                                    inputProps={{
                                        step: 300,
                                        'aria-label': 'Scheduled time',
                                    }}
                                />
                            </Form.Group>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions
                titlePrimary={loading ? 'Creating...' : 'Create'}
                titleSecondary="Cancel"
                onClickPrimary={onSave}
                onClickSecondary={onClose}
                disablePrimaryButton={!isValid || loading}
                disableSecondaryButton={loading}
            />
        </Dialog>
    );
};

CreateReportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};

export default CreateReportDialog;
